import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import styles from "./AddMappingModal.module.css";

// Mock hotels - replace with API call
const HOTELS = [
  { id: 1, name: "Burj Al Arab Jumeirah" },
  { id: 2, name: "The Ritz-Carlton" },
  { id: 3, name: "Marina Bay Sands" },
  { id: 4, name: "Hotel Plaza Athénée" },
  { id: 5, name: "Atlantis The Palm" },
  { id: 6, name: "Four Seasons Hotel" }
];

const API_SUPPLIERS = ["Amadeus GDS", "Sabre", "Booking.com", "Expedia", "Hotelbeds"];
const BED_TYPES = ["King", "Queen", "Twin", "Double", "Single"];

const emptyForm = {
  hotelId: "",
  hotel: "",
  roomType: "",
  roomTypeCode: "",
  bedType: "",
  maxOccupancy: 2,
  roomSize: "",
  apiSupplier: "",
  apiRoomCode: "",
  status: "active"
};

const AddMappingModal = ({ open, onClose, onSave, mapping }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (mapping) {
      setForm({
        hotelId: mapping.hotelId || "",
        hotel: mapping.hotel || "",
        roomType: mapping.roomType || "",
        roomTypeCode: mapping.roomTypeCode || "",
        bedType: mapping.bedType || "",
        maxOccupancy: mapping.maxOccupancy || 2,
        roomSize: mapping.roomSize || "",
        apiSupplier: mapping.apiSupplier || "",
        apiRoomCode: mapping.apiRoomCode || "",
        status: mapping.status || "active"
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [mapping, open]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const handleHotelChange = (hotelId) => {
    const hotel = HOTELS.find(h => h.id === parseInt(hotelId));
    setForm(prev => ({
      ...prev,
      hotelId: hotelId,
      hotel: hotel ? hotel.name : ""
    }));
    if (errors.hotelId) {
      setErrors(prev => ({ ...prev, hotelId: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "hotelId":
        if (!value) error = "Hotel is required";
        break;
      case "roomType":
        if (!value || !value.trim()) error = "Room type is required";
        break;
      case "roomTypeCode":
        if (!value || !value.trim()) {
          error = "Room type code is required";
        } else if (!/^[A-Z0-9_]+$/.test(value)) {
          error = "Code must contain only uppercase letters, numbers, and underscores";
        }
        break;
      case "bedType":
        if (!value) error = "Bed type is required";
        break;
      case "maxOccupancy":
        if (!value || value < 1 || value > 10) error = "Max occupancy must be between 1-10";
        break;
      case "roomSize":
        if (!value || value <= 0) error = "Room size is required";
        break;
      case "apiSupplier":
        if (!value) error = "API supplier is required";
        break;
      case "apiRoomCode":
        if (!value || !value.trim()) error = "API room code is required";
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    const fieldsToValidate = ["hotelId", "roomType", "roomTypeCode", "bedType", "maxOccupancy", "roomSize", "apiSupplier", "apiRoomCode"];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, form[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = () => {
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    const payload = {
      hotelId: parseInt(form.hotelId),
      hotel: form.hotel,
      roomType: form.roomType.trim(),
      roomTypeCode: form.roomTypeCode.trim().toUpperCase(),
      bedType: form.bedType,
      maxOccupancy: parseInt(form.maxOccupancy),
      roomSize: parseFloat(form.roomSize),
      apiSupplier: form.apiSupplier,
      apiRoomCode: form.apiRoomCode.trim(),
      status: form.status
    };

    onSave(payload);
    handleClose();
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={mapping ? "Edit Room Mapping" : "Add New Room Mapping"}
      width="800px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit}>
            💾 Save Mapping
          </button>
        </>
      }
    >
      <div className={styles.iconHeader}>
        <div className={styles.icon}>🔗</div>
        <div className={styles.headerText}>
          <h3>{mapping ? "Edit Room Mapping" : "Add New Room Mapping"}</h3>
          <p>
            {mapping 
              ? "Update room type mapping between hotel and API supplier"
              : "Create a new mapping to link hotel room types with API supplier codes"}
          </p>
        </div>
      </div>

      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) => 
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🏨 HOTEL & ROOM TYPE</h4>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>HOTEL <span className={styles.required}>*</span></label>
            <select
              value={form.hotelId}
              onChange={e => handleHotelChange(e.target.value)}
              onBlur={() => handleBlur("hotelId")}
              className={errors.hotelId ? styles.inputError : ""}
            >
              <option value="">Select hotel</option>
              {HOTELS.map(hotel => (
                <option key={hotel.id} value={hotel.id}>{hotel.name}</option>
              ))}
            </select>
            {errors.hotelId && <span className={styles.errorText}>{errors.hotelId}</span>}
          </div>

          <div className={styles.field}>
            <label>ROOM TYPE <span className={styles.required}>*</span></label>
            <input
              value={form.roomType}
              onChange={e => handleChange("roomType", e.target.value)}
              onBlur={() => handleBlur("roomType")}
              placeholder="e.g., Deluxe Suite"
              className={errors.roomType ? styles.inputError : ""}
            />
            {errors.roomType && <span className={styles.errorText}>{errors.roomType}</span>}
          </div>

          <div className={styles.field}>
            <label>ROOM TYPE CODE <span className={styles.required}>*</span></label>
            <input
              value={form.roomTypeCode}
              onChange={e => handleChange("roomTypeCode", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("roomTypeCode")}
              placeholder="DLX"
              className={`${styles.codeInput} ${errors.roomTypeCode ? styles.inputError : ""}`}
            />
            {errors.roomTypeCode && <span className={styles.errorText}>{errors.roomTypeCode}</span>}
            <span className={styles.helpText}>Uppercase letters, numbers, and underscores only</span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🛏️ ROOM DETAILS</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>BED TYPE <span className={styles.required}>*</span></label>
            <select
              value={form.bedType}
              onChange={e => handleChange("bedType", e.target.value)}
              onBlur={() => handleBlur("bedType")}
              className={errors.bedType ? styles.inputError : ""}
            >
              <option value="">Select bed type</option>
              {BED_TYPES.map(bed => (
                <option key={bed} value={bed}>{bed}</option>
              ))}
            </select>
            {errors.bedType && <span className={styles.errorText}>{errors.bedType}</span>}
          </div>

          <div className={styles.field}>
            <label>MAX OCCUPANCY <span className={styles.required}>*</span></label>
            <input
              type="number"
              value={form.maxOccupancy}
              onChange={e => handleChange("maxOccupancy", e.target.value)}
              onBlur={() => handleBlur("maxOccupancy")}
              min="1"
              max="10"
              className={errors.maxOccupancy ? styles.inputError : ""}
            />
            {errors.maxOccupancy && <span className={styles.errorText}>{errors.maxOccupancy}</span>}
            <span className={styles.helpText}>Maximum number of guests</span>
          </div>

          <div className={styles.field}>
            <label>ROOM SIZE (sqm) <span className={styles.required}>*</span></label>
            <input
              type="number"
              value={form.roomSize}
              onChange={e => handleChange("roomSize", e.target.value)}
              onBlur={() => handleBlur("roomSize")}
              placeholder="42"
              min="1"
              className={errors.roomSize ? styles.inputError : ""}
            />
            {errors.roomSize && <span className={styles.errorText}>{errors.roomSize}</span>}
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <div className={styles.statusToggle}>
              <input
                type="checkbox"
                id="mappingStatus"
                checked={form.status === "active"}
                onChange={e => handleChange("status", e.target.checked ? "active" : "inactive")}
                className={styles.toggleInput}
              />
              <label htmlFor="mappingStatus" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch}></span>
                <span className={styles.toggleText}>
                  {form.status === "active" ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
            <span className={styles.helpText}>Only active mappings sync inventory</span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🔗 API MAPPING</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>API SUPPLIER <span className={styles.required}>*</span></label>
            <select
              value={form.apiSupplier}
              onChange={e => handleChange("apiSupplier", e.target.value)}
              onBlur={() => handleBlur("apiSupplier")}
              className={errors.apiSupplier ? styles.inputError : ""}
            >
              <option value="">Select supplier</option>
              {API_SUPPLIERS.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
            {errors.apiSupplier && <span className={styles.errorText}>{errors.apiSupplier}</span>}
          </div>

          <div className={styles.field}>
            <label>API ROOM CODE <span className={styles.required}>*</span></label>
            <input
              value={form.apiRoomCode}
              onChange={e => handleChange("apiRoomCode", e.target.value)}
              onBlur={() => handleBlur("apiRoomCode")}
              placeholder="KDLX-NS"
              className={`${styles.apiCodeInput} ${errors.apiRoomCode ? styles.inputError : ""}`}
            />
            {errors.apiRoomCode && <span className={styles.errorText}>{errors.apiRoomCode}</span>}
            <span className={styles.helpText}>Exact code from API supplier system</span>
          </div>
        </div>
      </div>

      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Each mapping links one hotel room type to one API supplier room code</li>
            <li>Room type codes must be unique within each hotel</li>
            <li>API room codes must match exact supplier system codes</li>
            <li>Only active mappings will sync inventory and rates</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddMappingModal;